
import { useState } from 'react';
import { startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { useEvents, useUpdateEvent } from '@/hooks/useEvents';
import { useA2AProtocol } from '@/hooks/useA2AProtocol';
import { toast } from 'sonner';
import { CalendarEvent } from '@/types/calendar';
import { 
  UseCalendarEventsResult, 
  EventClickInfo, 
  EventChangeInfo 
} from '../types/calendar';

export const useCalendarEvents = (selectedDate: Date, visibleTypes: string[]): UseCalendarEventsResult => {
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  
  const startDate = startOfMonth(subMonths(selectedDate, 1));
  const endDate = endOfMonth(addMonths(selectedDate, 1));
  const { data: events, isLoading: eventsLoading } = useEvents(startDate, endDate);
  const updateEvent = useUpdateEvent();
  const { sendMessage } = useA2AProtocol();

  // Format events for FullCalendar with AI/Manual source tracking
  const formattedEvents = events?.filter(event => 
    !event.type || visibleTypes.includes(event.type)
  ).map(event => ({
    id: event.id,
    title: event.title,
    start: event.start_time,
    end: event.end_time,
    extendedProps: {
      ...event,
      type: event.type || 'other',
      description: event.description,
      location: event.location,
      attendees: event.attendees,
      source: event.source || 'manual',
      ai_agent: event.ai_agent
    },
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textColor: 'inherit',
    allDay: false
  }));

  const handleEventClick = (eventInfo: EventClickInfo) => {
    setSelectedEvent(eventInfo.event.extendedProps);
  };

  const handleEventChange = async (eventInfo: EventChangeInfo) => {
    const { event } = eventInfo;
    
    try {
      const response = await sendMessage({
        content: `Check for conflicts for event ${event.title} at ${event.start.toISOString()} to ${event.end.toISOString()}`,
        toAgent: 'scheduling-agent'
      });
      
      if (response && response.metadata?.hasConflict) {
        toast.error("Scheduling conflict detected", {
          description: response.content || "This time slot conflicts with another event."
        });
        eventInfo.revert();
        return;
      }

      const updatedEvent = {
        id: event.id,
        title: event.title,
        start_time: event.start.toISOString(),
        end_time: event.end.toISOString(),
        ...event.extendedProps
      };
      
      await updateEvent.mutateAsync(updatedEvent);
      toast.success("Event updated successfully");
    } catch (error) {
      console.error("Error updating event:", error);
      toast.error("Failed to update event");
      eventInfo.revert();
    }
  };

  const sendReminderForEvent = async (event: CalendarEvent) => {
    try {
      toast.info("Sending reminder...");
      
      const response = await sendMessage({
        content: `Send reminder for event: ${event.title}`,
        toAgent: 'scheduling-agent'
      });
      
      if (response && !response.metadata?.error) {
        toast.success("Reminder sent successfully");
      } else {
        toast.error("Failed to send reminder", {
          description: response?.content || "An error occurred"
        });
      }
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast.error("Failed to send reminder");
    }
  };

  const handleEventEdit = () => {
    // Force refresh of events after edit
    // The query will automatically refresh due to invalidation in useUpdateEvent
  };

  const handleEventDelete = () => {
    setSelectedEvent(null);
    // Force refresh of events after delete
    // The query will automatically refresh due to invalidation in useDeleteEvent
  };

  return {
    events: formattedEvents || [],
    isLoading: eventsLoading,
    selectedEvent,
    setSelectedEvent,
    handleEventClick,
    handleEventChange,
    sendReminderForEvent,
    handleEventEdit,
    handleEventDelete
  };
};
